import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '@/store/languageStore';
import { PageBackButton } from '@/components/common/PageBackButton';
import toast from 'react-hot-toast';
import { useQuery, useQueryClient } from 'react-query';
import { SwatchIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { themeSettingsApi, type PartnerOption } from '@/services/theme/themeSettingsApi';
import { getConfig } from '@/services/api/configService';
import { DEFAULT_THEME } from '@/utils/themeDerivation';
import { ThemeColorEditor } from './theme/ThemeColorEditor';
import { ThemePreviewPanel } from './theme/ThemePreviewPanel';
import { ThemeModeToggle } from './theme/ThemeModeToggle';
import { useThemeDraft } from './theme/useThemeDraft';

type Tab = 'global' | 'partner';
type ThemeMode = 'light' | 'dark';

const ThemeSettingsPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>('global');
  const [activeMode, setActiveMode] = useState<ThemeMode>('light');
  const [partners, setPartners] = useState<PartnerOption[]>([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [globalEnabled, setGlobalEnabled] = useState(false);
  const [partnerEnabled, setPartnerEnabled] = useState(false);

  const { draftTheme, isDirty, resetDraft, commitDraft, loadTheme, updatePalette } = useThemeDraft();

  useQuery(['config', 'theme-settings'], getConfig, { staleTime: 60_000 });

  const { data: context } = useQuery('theme-settings-context', themeSettingsApi.getContext);

  const canManageGlobal = context?.canManageGlobal ?? false;
  const editablePartnerId = context?.editablePartnerOrganizationId ?? null;

  const loadGlobal = useCallback(async () => {
    const theme = await themeSettingsApi.getGlobalTheme();
    loadTheme(theme);
  }, [loadTheme]);

  const loadPartner = useCallback(
    async (orgId: number) => {
      const res = await themeSettingsApi.getOrganizationTheme(orgId);
      loadTheme(res.theme);
      setPartnerEnabled(res.enabled);
    },
    [loadTheme],
  );

  useEffect(() => {
    if (!context) return;

    setGlobalEnabled(context.globalThemeEnabled);

    const init = async () => {
      setLoading(true);
      try {
        if (context.canManageGlobal) {
          const partnerList = await themeSettingsApi.listPartners();
          setPartners(partnerList);
          setActiveTab('global');
          await loadGlobal();
        } else if (context.editablePartnerOrganizationId) {
          setActiveTab('partner');
          setSelectedPartnerId(context.editablePartnerOrganizationId);
          await loadPartner(context.editablePartnerOrganizationId);
        } else {
          loadTheme(DEFAULT_THEME);
        }
      } catch (err: any) {
        toast.error(err.response?.data?.error ?? t('themeSettings.loadError'));
      } finally {
        setLoading(false);
      }
    };

    void init();
  }, [context, loadGlobal, loadPartner, loadTheme, t]);

  const handleTabChange = async (tab: Tab) => {
    if (isDirty && !window.confirm(t('themeSettings.unsavedConfirm'))) return;
    setActiveTab(tab);
    setLoading(true);
    try {
      if (tab === 'global') {
        await loadGlobal();
      } else {
        const orgId = selectedPartnerId ?? partners[0]?.id ?? editablePartnerId;
        if (orgId) {
          setSelectedPartnerId(orgId);
          await loadPartner(orgId);
        }
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error ?? t('themeSettings.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handlePartnerChange = async (orgId: number) => {
    if (isDirty && !window.confirm(t('themeSettings.unsavedConfirm'))) return;
    setSelectedPartnerId(orgId);
    setLoading(true);
    try {
      await loadPartner(orgId);
    } catch (err: any) {
      toast.error(err.response?.data?.error ?? t('themeSettings.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const needsActivation = activeTab === 'global' ? !globalEnabled : !partnerEnabled;

  const handleSave = async () => {
    setSaving(true);
    try {
      if (activeTab === 'global' && canManageGlobal) {
        const saved = await themeSettingsApi.updateGlobalTheme(draftTheme);
        commitDraft(saved);
        setGlobalEnabled(true);
      } else if (activeTab === 'partner' && selectedPartnerId) {
        const saved = await themeSettingsApi.updateOrganizationTheme(selectedPartnerId, draftTheme);
        commitDraft(saved);
        setPartnerEnabled(true);
      } else {
        throw new Error('Nothing to save');
      }
      toast.success(
        needsActivation ? t('themeSettings.saveSuccessActivated') : t('themeSettings.saveSuccess'),
      );
      await queryClient.refetchQueries('config');
    } catch (err: any) {
      toast.error(err.response?.data?.error ?? t('themeSettings.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const showPartnerTab = canManageGlobal || editablePartnerId !== null;

  if (loading && !context) {
    return (
      <div className="p-6 text-textSecondary">{t('common.loading')}</div>
    );
  }

  return (
    <div className="p-4 xl:p-6 w-full max-w-[1920px] mx-auto space-y-6">
      <div className="flex items-start gap-3">
        <PageBackButton />
        <SwatchIcon className="w-8 h-8 text-bgPrimary shrink-0" />
        <div>
          <h1 className="text-2xl font-bold text-textDefault">{t('themeSettings.title')}</h1>
          <p className="text-textSecondary mt-1">{t('themeSettings.description')}</p>
        </div>
      </div>

      {needsActivation && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-400/50 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-900 dark:text-amber-100">
          <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">{t('themeSettings.disabledBannerTitle')}</p>
            <p className="text-xs mt-1 opacity-90">
              {activeTab === 'partner'
                ? t('themeSettings.disabledBannerBodyPartner')
                : t('themeSettings.disabledBannerBody')}
            </p>
          </div>
        </div>
      )}

      <div className="flex gap-2 border-b border-border">
        {canManageGlobal && (
          <button
            type="button"
            onClick={() => void handleTabChange('global')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'global'
                ? 'border-bgPrimary text-textPrimary'
                : 'border-transparent text-textSecondary hover:text-textDefault'
            }`}
          >
            {t('themeSettings.tabGlobal')}
          </button>
        )}
        {showPartnerTab && (
          <button
            type="button"
            onClick={() => void handleTabChange('partner')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'partner'
                ? 'border-bgPrimary text-textPrimary'
                : 'border-transparent text-textSecondary hover:text-textDefault'
            }`}
          >
            {t('themeSettings.tabPartner')}
          </button>
        )}
      </div>

      {activeTab === 'partner' && canManageGlobal && partners.length > 0 && (
        <div className="max-w-md">
          <label className="block text-sm text-textSecondary mb-1">{t('themeSettings.selectPartner')}</label>
          <select
            value={selectedPartnerId ?? ''}
            onChange={(e) => void handlePartnerChange(Number(e.target.value))}
            className="w-full rounded border border-border bg-bgElevated px-3 py-2 text-sm text-textDefault"
          >
            {partners.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <div className="text-textSecondary">{t('common.loading')}</div>
      ) : (
        <div className="flex flex-col xl:flex-row gap-6 xl:items-start">
          {/* Editor — smal vänsterkolumn */}
          <div className="w-full xl:w-[440px] xl:shrink-0 space-y-4 order-2 xl:order-1">
            <div className="rounded-xl border border-border bg-bgElevated shadow-sm overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-border bg-bgContainer/60">
                <div>
                  <h2 className="text-base font-semibold text-textDefault">
                    {activeMode === 'light' ? t('themeSettings.lightTitle') : t('themeSettings.darkTitle')}
                  </h2>
                  <p className="text-xs text-textSecondary mt-0.5">{t('themeSettings.editModeHint')}</p>
                </div>
                <ThemeModeToggle mode={activeMode} onChange={setActiveMode} t={t} />
              </div>
              <div className="p-5">
                <ThemeColorEditor
                  palette={draftTheme[activeMode]}
                  onChange={(field, value) => updatePalette(activeMode, field, value)}
                  t={t}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={resetDraft}
                disabled={!isDirty || saving}
                className="rounded-lg border border-border px-4 py-2 text-sm text-textDefault hover:bg-bgContainer disabled:opacity-40"
              >
                {t('themeSettings.reset')}
              </button>
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={saving}
                className="rounded-lg bg-bgPrimary px-4 py-2 text-sm font-medium text-textLight hover:opacity-90 disabled:opacity-40"
              >
                {saving
                  ? t('common.saving')
                  : needsActivation && !isDirty
                    ? t('themeSettings.activate')
                    : !isDirty
                      ? t('themeSettings.reapply')
                      : t('common.save')}
              </button>
            </div>
          </div>

          {/* Preview — tar resterande bredd */}
          <div className="flex-1 min-w-0 order-1 xl:order-2 xl:sticky xl:top-4">
            <div className="rounded-xl border border-border bg-bgElevated p-4 xl:p-5 shadow-sm h-full">
              <ThemePreviewPanel draftTheme={draftTheme} previewMode={activeMode} t={t} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemeSettingsPage;
